/**
 * iOS Safari viewport height fix and modal scroll prevention
 * Sets CSS variables for accurate viewport height across all iOS versions
 * Prevents background scrolling when modals are open
 * Fixes modal height issues when address bar collapses/expands
 */
(function() {
    let scrollY = 0;

    /**
     * Update CSS variables used for viewport sizing and scrollbar width.
     * Sets `--vh` to 1% of the current viewport height and `--scrollbar-width` to
     * the current scrollbar width to avoid layout shift when locking body.
     * @returns {void}
     */
    function updateViewportHeight() {
        // Get actual viewport height (updates when address bar changes)
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);

        // Calculate scrollbar width to prevent layout shift
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    }

    /**
     * Prevent scrolls/touchmoves outside of allowed modal and tray content when a modal is open.
     * Allows normal scrolling inside Bootstrap modal content, PowerReviews content, and the mini cart tray.
     * @param {Event} e The touch event to evaluate and potentially prevent.
     * @returns {void}
     */
    function preventBackgroundScroll(e) {
        // Allow scrolling within Bootstrap modal content, PowerReviews content, or the mini cart tray
        if (e.target.closest('.modal-content, .pr-media-card-content, .mk-mini-cart__tray-container')) {
            return;
        }

        // Prevent all other touch scrolling
        e.preventDefault();
    }

    /**
     * Determine whether a mutated node is a PowerReviews media card or
     * contains one within its subtree.
     * @param {Node} node The added or removed DOM node from a mutation record.
     * @returns {boolean} True when the node is or contains `.pr-media-card`.
     */
    function isOrContainsPRMediaCard(node) {
        if (!node || node.nodeType !== Node.ELEMENT_NODE) {
            return false;
        }
        return node.matches('.pr-media-card') || !!node.querySelector('.pr-media-card');
    }

    /**
     * MutationObserver that applies/removes the same page scroll lock pattern
     * used by Bootstrap modals when PowerReviews media card opens/closes.
     * whenever PowerReviews injects or removes `.pr-media-card` from the DOM.
     * PowerReviews uses its own lightbox (not Bootstrap modals), so Bootstrap
     * lifecycle events cannot be used here.
     */
    var prScrollGuardActive = false;
    var prMediaCardObserver = window.MutationObserver ? new window.MutationObserver(function(mutations) {
        var hasPRCardChange = mutations.some(function(mutation) {
            return Array.prototype.some.call(mutation.addedNodes, isOrContainsPRMediaCard)
                || Array.prototype.some.call(mutation.removedNodes, isOrContainsPRMediaCard);
        });
        if (!hasPRCardChange) {
            return;
        }
        var hasPRCard = !!document.querySelector('.pr-media-card');
        if (hasPRCard && !prScrollGuardActive) {
            scrollY = window.scrollY;
            document.body.classList.add('modal-open');
            document.documentElement.style.overflow = 'hidden';
            document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
            document.body.style.top = `-${scrollY}px`;
            prScrollGuardActive = true;
        } else if (!hasPRCard && prScrollGuardActive) {
            var hasBootstrapModal = !!document.querySelector('.modal.show');
            if (!hasBootstrapModal) {
                document.body.classList.remove('modal-open');
                document.documentElement.style.overflow = '';
                document.body.style.top = '';
                window.scrollTo(0, scrollY);
                document.removeEventListener('touchmove', preventBackgroundScroll);
            }
            prScrollGuardActive = false;
        }
    }) : null;

    // ========== Viewport Height Management ==========
    // Update on all relevant events (ATTACHED ONCE)
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    // Visual viewport API for iOS (more accurate for address bar changes)
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', updateViewportHeight);
        window.visualViewport.addEventListener('scroll', updateViewportHeight);
    }

    // ========== Modal Scroll Prevention ==========
    // Watch for modal open/close (ATTACHED ONCE)
    $(document).on('show.bs.modal', '.modal', function() {
        // Save current scroll position
        scrollY = window.scrollY;

        // Add touch event listener to prevent background scroll
        document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

        // Set body position
        document.body.style.top = `-${scrollY}px`;
    });

    $(document).on('hidden.bs.modal', '.modal', function() {
        // Remove touch event listener
        document.removeEventListener('touchmove', preventBackgroundScroll);

        // Restore scroll position
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
    });

    var isPrMediaCardObserverActive = false;
    /**
     * Start the PR media card observer only on pages that use PowerReviews.
     * Gates on actual PR DOM mount points rather than window flags (which are
     * set on every page when prEnabled is true) to avoid site-wide overhead.
     * @returns {void}
     */
    function initPrMediaCardObserver() {
        if (!prMediaCardObserver || !document.body || isPrMediaCardObserverActive) {
            return;
        }

        if (!document.querySelector('[data-powerreviews-root], #pr-reviewdisplay, #pr-reviewsnippet, .pr-pdp-components')) {
            return;
        }

        prMediaCardObserver.observe(document.body, { childList: true, subtree: true });
        isPrMediaCardObserverActive = true;
    }

    // ========== PR Media Card Scroll Guard ==========
    // Start observing once the DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            initPrMediaCardObserver();
        });
    } else {
        initPrMediaCardObserver();
    }

    // ========== Initial Setup ==========
    // Update viewport height on page load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', updateViewportHeight);
    } else {
        updateViewportHeight();
    }
})();
